import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX, 
  ChevronLeft, 
  ChevronRight,
  List,
  X,
  Headphones,
  Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import axios from 'axios';

// Use current origin for deployed environment
const getAssetBaseUrl = () => window.location.origin;
const BACKEND_URL = getAssetBaseUrl();
const API = `${BACKEND_URL}/api`;

const AudiobookPlayer = ({ 
  audiobook, 
  chapters = [], 
  onClose,
  isMinimized = false,
  onToggleMinimize 
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [showChapterList, setShowChapterList] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const lastProgressSentRef = useRef(0);
  const token = localStorage.getItem('token');

  // Track reading/listening progress
  const trackProgress = useCallback(async (percentage) => {
    if (!audiobook?.id || !token) return;
    
    // Only send progress updates every 10% to avoid too many requests
    const roundedProgress = Math.floor(percentage / 10) * 10;
    if (roundedProgress <= lastProgressSentRef.current) return;
    
    lastProgressSentRef.current = roundedProgress;
    
    try {
      await axios.post(
        `${API}/books/${audiobook.id}/progress`,
        { progress_percentage: percentage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.log('Failed to track progress:', err);
    }
  }, [audiobook?.id, token]);

  // Get current audio URL
  const getCurrentAudioUrl = () => {
    const baseUrl = getAssetBaseUrl();
    if (chapters && chapters.length > 0) {
      return `${baseUrl}${chapters[currentChapterIndex]?.url}?token=${token}`;
    }
    return audiobook?.audiobook_file_url 
      ? `${baseUrl}${audiobook.audiobook_file_url}?token=${token}` 
      : null;
  };

  // Format time to MM:SS or HH:MM:SS
  const formatTime = (time) => {
    if (isNaN(time) || !isFinite(time) || time < 0) return '--:--';
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle play/pause
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle seek
  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  // Handle volume change
  const handleVolumeChange = (value) => {
    if (audioRef.current) {
      audioRef.current.volume = value[0];
      setVolume(value[0]);
      setIsMuted(value[0] === 0);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume || 1;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  // Skip forward/backward
  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        Math.max(audioRef.current.currentTime + seconds, 0),
        duration
      );
    }
  };

  // Change chapter
  const changeChapter = (index) => {
    if (index >= 0 && index < chapters.length) {
      setCurrentChapterIndex(index);
      setCurrentTime(0);
      setShowChapterList(false);
      // Auto-play when changing chapters
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }, 100);
    }
  };

  // Previous/Next chapter
  const prevChapter = () => changeChapter(currentChapterIndex - 1);
  const nextChapter = () => changeChapter(currentChapterIndex + 1);

  // Change playback rate
  const cyclePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setPlaybackRate(rates[nextIndex]);
    if (audioRef.current) {
      audioRef.current.playbackRate = rates[nextIndex];
    }
  };

  // Update time and track progress
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      
      // Track progress based on overall listening
      if (audio.duration > 0 && chapters.length > 0) {
        // Calculate overall progress across all chapters
        const chapterProgress = (currentChapterIndex / chapters.length) * 100;
        const currentChapterProgress = (audio.currentTime / audio.duration) * (100 / chapters.length);
        const totalProgress = chapterProgress + currentChapterProgress;
        trackProgress(Math.min(totalProgress, 100));
      } else if (audio.duration > 0) {
        // Single audio file
        const progress = (audio.currentTime / audio.duration) * 100;
        trackProgress(progress);
      }
    };
    
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      // Auto-play next chapter
      if (chapters.length > 0 && currentChapterIndex < chapters.length - 1) {
        nextChapter();
      } else {
        setIsPlaying(false);
        // Mark as completed when reaching end of last chapter
        trackProgress(100);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentChapterIndex, chapters.length, trackProgress]);

  const audioUrl = getCurrentAudioUrl();
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const currentChapter = chapters[currentChapterIndex];

  if (isMinimized) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-900 to-purple-800 border-t border-purple-700 p-3 z-50">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src={audiobook?.cover_image_url?.startsWith('http') ? audiobook.cover_image_url : `${BACKEND_URL}${audiobook?.cover_image_url}`}
              alt={audiobook?.title}
              className="w-12 h-12 rounded object-cover"
            />
            <div className="hidden sm:block">
              <p className="text-white font-medium text-sm truncate max-w-[200px]">{audiobook?.title}</p>
              <p className="text-purple-300 text-xs">{currentChapter?.title || 'Playing'}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="icon" onClick={() => skip(-10)} className="text-white hover:bg-purple-700">
              <SkipBack className="h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={togglePlay} 
              className="text-white bg-purple-600 hover:bg-purple-500 rounded-full w-10 h-10"
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => skip(10)} className="text-white hover:bg-purple-700">
              <SkipForward className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-purple-300 text-xs hidden sm:block">{formatTime(currentTime)} / {formatTime(duration)}</span>
            <Button variant="ghost" size="icon" onClick={onToggleMinimize} className="text-white hover:bg-purple-700">
              <ChevronLeft className="h-5 w-5 rotate-90" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-purple-700">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Mini progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-purple-950">
          <div 
            className="h-full bg-gradient-to-r from-purple-400 to-pink-500 transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>

        <audio ref={audioRef} src={audioUrl} preload="metadata" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-purple-800 border-b border-purple-700 p-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-purple-700">
            <X className="h-6 w-6" />
          </Button>
          <div className="flex items-center space-x-2">
            <Headphones className="h-6 w-6 text-purple-300" />
            <span className="text-white font-semibold">Audiobook Player</span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onToggleMinimize} className="text-white hover:bg-purple-700">
          <ChevronRight className="h-6 w-6 -rotate-90" />
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Player Section */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gradient-to-b from-purple-900/50 to-black">
          {/* Album Art */}
          <div className="relative mb-8">
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-lg overflow-hidden shadow-2xl shadow-purple-500/30">
              <img 
                src={audiobook?.cover_image_url?.startsWith('http') ? audiobook.cover_image_url : `${BACKEND_URL}${audiobook?.cover_image_url}`}
                alt={audiobook?.title}
                className="w-full h-full object-cover"
              />
            </div>
            {/* Spinning vinyl effect when playing */}
            {isPlaying && (
              <div className="absolute -inset-4 border-4 border-purple-500/30 rounded-full animate-spin" style={{ animationDuration: '8s' }} />
            )}
          </div>

          {/* Book Info */}
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{audiobook?.title}</h1>
            <p className="text-purple-300">{audiobook?.author}</p>
            {audiobook?.narrator && (
              <p className="text-purple-400 text-sm mt-1">Narrated by {audiobook.narrator}</p>
            )}
            {currentChapter && (
              <p className="text-purple-200 mt-2 text-sm bg-purple-900/50 px-3 py-1 rounded-full inline-block">
                Chapter {currentChapterIndex + 1}: {currentChapter.title}
              </p>
            )}
          </div>

          {/* Progress Bar */}
          <div className="w-full max-w-xl px-4 mb-6">
            <Slider
              value={[currentTime]}
              max={duration || 100}
              step={1}
              onValueChange={handleSeek}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-purple-300 mt-2">
              <span>{formatTime(currentTime)}</span>
              <span>-{formatTime(duration - currentTime)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-4">
            {/* Playback Rate */}
            <Button 
              variant="ghost" 
              onClick={cyclePlaybackRate}
              className="text-purple-300 hover:bg-purple-900/50 text-sm font-mono"
            >
              {playbackRate}x
            </Button>

            {/* Previous Chapter */}
            {chapters.length > 1 && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={prevChapter}
                disabled={currentChapterIndex === 0}
                className="text-white hover:bg-purple-900/50 disabled:opacity-30"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            )}

            {/* Skip Back */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => skip(-10)}
              className="text-white hover:bg-purple-900/50"
            >
              <SkipBack className="h-6 w-6" />
            </Button>

            {/* Play/Pause */}
            <Button 
              size="lg"
              onClick={togglePlay}
              className="bg-purple-600 hover:bg-purple-500 text-white rounded-full w-16 h-16 shadow-lg shadow-purple-500/50"
            >
              {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8 ml-1" />}
            </Button>

            {/* Skip Forward */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => skip(10)}
              className="text-white hover:bg-purple-900/50"
            >
              <SkipForward className="h-6 w-6" />
            </Button>

            {/* Next Chapter */}
            {chapters.length > 1 && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={nextChapter}
                disabled={currentChapterIndex === chapters.length - 1}
                className="text-white hover:bg-purple-900/50 disabled:opacity-30"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            )}

            {/* Volume */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleMute}
              className="text-white hover:bg-purple-900/50"
            >
              {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </Button>
          </div>

          {/* Volume Slider */}
          <div className="w-32 mt-4">
            <Slider
              value={[isMuted ? 0 : volume]}
              max={1}
              step={0.01}
              onValueChange={handleVolumeChange}
              className="w-full"
            />
          </div>
        </div>

        {/* Chapter List Sidebar */}
        {chapters.length > 1 && (
          <div className="w-full lg:w-80 bg-purple-950/50 border-l border-purple-800 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-purple-800 flex items-center space-x-2">
              <List className="h-5 w-5 text-purple-300" />
              <h3 className="text-white font-semibold">Chapters ({chapters.length})</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {chapters.map((chapter, index) => (
                <button
                  key={index}
                  onClick={() => changeChapter(index)}
                  className={`w-full p-4 text-left border-b border-purple-900/50 transition-colors ${
                    index === currentChapterIndex 
                      ? 'bg-purple-600/30 border-l-4 border-l-purple-400' 
                      : 'hover:bg-purple-900/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-medium ${index === currentChapterIndex ? 'text-purple-200' : 'text-white'}`}>
                        Chapter {index + 1}
                      </p>
                      <p className="text-sm text-purple-400">{chapter.title || `Part ${index + 1}`}</p>
                    </div>
                    {chapter.duration && (
                      <span className="text-xs text-purple-500 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {chapter.duration}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Hidden Audio Element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  );
};

export default AudiobookPlayer;
